import React, { useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const empty = {
  date: "", time: "", venue: "", court_type: "", level: "",
  price: "", booking_link: "", notes: "", total_slots: 4,
};

export const CreateMatchDialog = ({ playerName, onCreate, onRequireName }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const handleOpen = () => {
    if (!playerName) {
      onRequireName?.();
      return;
    }
    setOpen(true);
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.date && form.time && form.venue.trim();

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await onCreate({ ...form, organizer: playerName });
      setForm(empty);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        onClick={handleOpen}
        data-testid="create-match-button"
        className="bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-full h-12 px-6 shadow-lg shadow-[#ccff00]/20"
      >
        <Plus className="w-5 h-5 mr-1" /> Nuova partita
      </Button>
      <DialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#ccff00]" /> Organizza una partita
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Inserisci data, ora e luogo per creare la partita.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data">
              <Input type="date" value={form.date} onChange={(e) => set("date")(e.target.value)}
                data-testid="match-date-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
            </Field>
            <Field label="Ora">
              <Input type="time" value={form.time} onChange={(e) => set("time")(e.target.value)}
                data-testid="match-time-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
            </Field>
          </div>

          <Field label="Circolo / Campo">
            <Input placeholder="Es. Padel Club Milano" value={form.venue}
              onChange={(e) => set("venue")(e.target.value)}
              data-testid="match-venue-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo campo">
              <Select value={form.court_type} onValueChange={set("court_type")}>
                <SelectTrigger data-testid="match-court-type-select" className="bg-slate-900/60 border-slate-700 text-slate-100">
                  <SelectValue placeholder="Scegli" />
                </SelectTrigger>
                <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
                  <SelectItem value="Coperto">Coperto</SelectItem>
                  <SelectItem value="All'aperto">All'aperto</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Livello">
              <Select value={form.level} onValueChange={set("level")}>
                <SelectTrigger data-testid="match-level-select" className="bg-slate-900/60 border-slate-700 text-slate-100">
                  <SelectValue placeholder="Scegli" />
                </SelectTrigger>
                <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
                  <SelectItem value="Tutti">Tutti</SelectItem>
                  <SelectItem value="Principiante">Principiante</SelectItem>
                  <SelectItem value="Intermedio">Intermedio</SelectItem>
                  <SelectItem value="Avanzato">Avanzato</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Prezzo a testa">
              <Input placeholder="Es. 10€" value={form.price}
                onChange={(e) => set("price")(e.target.value)}
                data-testid="match-price-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
            </Field>
            <Field label="Posti totali">
              <Select value={String(form.total_slots)} onValueChange={(v) => set("total_slots")(Number(v))}>
                <SelectTrigger data-testid="match-slots-select" className="bg-slate-900/60 border-slate-700 text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="4">4</SelectItem>
                  <SelectItem value="6">6</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Note (opzionale)">
            <Textarea placeholder="Es. Portate l'acqua, campo prenotato alle 19" value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              data-testid="match-notes-input" className="bg-slate-900/60 border-slate-700 text-slate-100 resize-none" rows={2} />
          </Field>

          <Field label="Prenotazione campo (link o nota)">
            <Input placeholder="Es. https://... oppure 'Campo 3 prenotato'" value={form.booking_link}
              onChange={(e) => set("booking_link")(e.target.value)}
              data-testid="match-booking-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
          </Field>
          <p className="text-xs text-slate-500">Come organizzatore occuperai automaticamente il primo posto.</p>
        </div>

        <DialogFooter>
          <Button
            disabled={!valid || busy}
            onClick={submit}
            data-testid="submit-match-button"
            className="w-full bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl h-11 disabled:opacity-40"
          >
            {busy ? "Creazione..." : "Crea partita"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</Label>
    {children}
  </div>
);
